import { Box } from '@mui/material';
import bwipjs from 'bwip-js/browser';
import Mustache from 'mustache';
import { useMemo } from 'react';
import { Barcode as BarcodeUtils } from 'shared/core';
import { asUTCDate } from 'shared/utils/DateTime';
import { useOrganizationGs1Prefix } from '../hooks/useOrganizationGs1Prefix';

/**
 * Props for the Barcode component
 */
type BarcodeProps = {
  // Basic props
  tagNumber: string;

  // Display configuration
  textTemplate?: string;
  scale?: number;
  textsize?: number;
  barCodeHeight?: number;
  height?: string;
  width?: number;
  inkspread?: number;
  marginTop?: string;
  marginBottom?: string;
  rotate?: 'N' | 'R' | 'L' | 'I';

  // Barcode content props
  tagType?: 'upc' | 'gs1' | 'sscc18' | string;
  productGS1?: string;
  packDate?: string;
  gtinCode?: string;
  numberSystem?: string;
  lotNumber?: string;
  productCode?: string;
  customFields?: { [key: string]: string | number };
};

/**
 * Renders vector SVG, NOT a canvas bitmap: a resampled canvas antialiases thin bars to grey and
 * the printer's 1-bit stage halftones that into broken bars.
 */
export default function Barcode({
  // Destructure props with logical grouping
  // Barcode type and identifiers
  tagType,
  tagNumber,

  // Barcode content
  packDate,
  gtinCode,
  productGS1,
  textTemplate,
  lotNumber,
  numberSystem,
  productCode,
  customFields,

  // Display configuration
  scale = 3,
  textsize = 5.5,
  barCodeHeight = 5,
  height = '100%',
  inkspread = 0.5,
  width = 100,
  marginTop = '10px',
  marginBottom,
  rotate,
}: BarcodeProps) {
  const gs1CompanyPrefix = useOrganizationGs1Prefix();

  const svgMarkup = useMemo(() => {
    try {
      const barcodeProps = getBarcodeProps(tagNumber, {
        type: tagType,
        textTemplate,
        orgInfo: { gs1CompanyPrefix },
        packDate,
        gtinCode,
        productGS1,
        numberSystem,
        lotNumber,
        productCode,
        customFields,
      });

      return bwipjs.toSVG({ scale, textsize, inkspread, rotate, height: barCodeHeight, ...barcodeProps });
    } catch (e) {
      console.error(`Failed to render barcode: ${e instanceof Error ? e.message : String(e)}`, e);
      return null;
    }
  }, [
    tagNumber,
    barCodeHeight,
    scale,
    textsize,
    inkspread,
    textTemplate,
    tagType,
    gs1CompanyPrefix,
    gtinCode,
    packDate,
    numberSystem,
    lotNumber,
    productCode,
    customFields,
    rotate,
    productGS1,
  ]);

  return (
    <Box sx={{ display: 'flex', width: '100%', flexGrow: 1, justifyContent: 'center' }}>
      {svgMarkup ? (
        // Wrapper is the sized flex item the parent Box centers; the SVG fills it.
        <div
          style={{ width, height, marginTop, marginBottom }}
          dangerouslySetInnerHTML={{ __html: withExplicitSvgSize(svgMarkup, '100%', '100%') }}
        />
      ) : (
        // Generation failed. Undrawn spacer; must stay a replaced element so the
        // cell keeps the flex basis it had, otherwise neighbouring cells reflow.
        <canvas style={{ width, height, marginTop, marginBottom }} />
      )}
    </Box>
  );
}

/** Set explicit `width`/`height`, `preserveAspectRatio="none"` and `display:block` on a bwip-js viewBox-only SVG, stripping pre-existing sizing attributes first so it is idempotent. `display:block` keeps the SVG off the text baseline, which would otherwise add a descender gap below it. */
export function withExplicitSvgSize(svg: string, width: number | string, height: number | string): string {
  const widthAttr = typeof width === 'number' ? `${width}px` : width;
  const heightAttr = typeof height === 'number' ? `${height}px` : height;

  return svg.replace(/<svg\b[^>]*>/, (openTag) => {
    const stripped = openTag.replace(/\s+(?:width|height|preserveAspectRatio|style)\s*=\s*(?:"[^"]*"|'[^']*')/gi, '');
    const inner = stripped.slice('<svg'.length, stripped.endsWith('/>') ? -2 : -1);
    const selfClosing = stripped.endsWith('/>');
    return `<svg width="${widthAttr}" height="${heightAttr}" preserveAspectRatio="none" style="display:block"${inner}${selfClosing ? '/>' : '>'}`;
  });
}

/**
 * Barcode type definitions and generation strategies
 */
const barcodeTypes = {
  // Custom UPC barcode
  custom_upc: {
    bcid: 'upca',
    requiresGS1: false,
    generate: ({ customFields }) => {
      console.debug('Generating custom_upc barcode', { customFields });
      return customFields.custom_upc;
    },
  },

  // Standard UPC barcode
  upc: {
    bcid: 'upca',
    requiresGS1: false,
    generate: ({ gtinCode, gs1CompanyPrefix, numberSystem = '0', productCode, customFields }) => {
      console.debug('Generating UPC barcode', { gtinCode, gs1CompanyPrefix, numberSystem, productCode });

      const customUpcLength = customFields?.custom_upc?.trim().length;

      if (customUpcLength === 10) {
        console.debug('Using custom UPC code from custom fields', { customUPC: customFields.custom_upc });

        return `${numberSystem}${customFields.custom_upc}`;
      }

      if (customUpcLength === 11 || customUpcLength === 12) {
        console.debug('Using custom UPC code from custom fields', { customUPC: customFields.custom_upc });

        return customFields.custom_upc;
      }

      // Handle direct GTIN code (UPC-A format)
      if (gtinCode?.length === 12) {
        console.debug('Using direct GTIN-12 code', { gtinCode });
        return gtinCode;
      }

      // Handle GTIN-14 converting GTIN-14 to 12s. Only works if the GS1 prefix has at least one leading 0
      if (gtinCode?.length === 14) {
        if (gtinCode.charAt(1) === '0') {
          console.warn('Converting GTIN-14 into GTIN-12', { gtinCode });

          const raw = gtinCode.slice(2, 13); // extract gs1 + item code
          const check = BarcodeUtils.upcCheckDigit(raw); // calculate check digit
          const result = `${raw}${check}`;

          console.debug('Converted GTIN-14 to UPC', { raw, check, result });

          return result;
        } else {
          console.warn(
            'Tried to convert a GTIN-14 to GTIN-12, but could not since GS1 prefix does not contain leading 0.',
            { gtinCode }
          );
        }
      }

      // Generate UPC-A from components
      console.debug('Generating UPC-A from components');
      const prefixLength = gs1CompanyPrefix?.length;
      const targetLength = 11 - numberSystem.length - prefixLength;
      const paddedProductCode = productCode?.toString().padStart(targetLength, '0').substring(0, targetLength);

      const upcCode = `${numberSystem}${gs1CompanyPrefix}${paddedProductCode}`;
      const checkDigit = BarcodeUtils.upcCheckDigit(upcCode);
      const result = `${upcCode}${checkDigit}`;

      console.debug('UPC-A generated', { prefixLength, targetLength, paddedProductCode, upcCode, checkDigit, result });

      return result;
    },
  },

  // GS1-128 barcode
  gs1: {
    bcid: 'gs1-128',
    requiresGS1: false,
    generate: ({ gtinCode, packDate, lotNumber }) => {
      console.debug('Generating GS1-128 barcode', { gtinCode, packDate, lotNumber });

      // Format pack date if provided
      const packDateFmt = packDate ? asUTCDate(packDate)?.toFormat('yyMMdd') : '';
      const packDateStr = packDateFmt ? `(13)${packDateFmt}` : '';
      console.debug('Pack date formatted', { packDate, packDateFmt, packDateStr });

      // Ensure GTIN is in correct format
      let validGtin = gtinCode;
      if (validGtin?.length < 14) {
        console.debug('GTIN needs padding to GTIN-14 format', { originalGtin: gtinCode });
        const gtin14Base = gtinCode?.padStart(13, '0');
        const checkDigit = BarcodeUtils.upcCheckDigit(gtin14Base);
        validGtin = `${gtin14Base}${checkDigit}`;
        console.debug('GTIN padded to GTIN-14', { gtin14Base, checkDigit, validGtin });
      }

      // Return formatted GS1-128 string
      const result = `(01)${validGtin}${packDateStr}(10)${lotNumber}`;
      console.debug('GS1-128 barcode generated', { result });
      return result;
    },
    props: { includecheck: true, includecheckintext: true, textxalign: 'center' },
  },

  // SSCC-18 barcode
  sscc18: {
    bcid: 'sscc18',
    requiresGS1: true,
    generate: ({ tagNumber, gs1CompanyPrefix }) => {
      console.debug('Generating SSCC-18 barcode with values:', {
        tagNumber: tagNumber,
        tagNumberType: typeof tagNumber,
        gs1CompanyPrefix: gs1CompanyPrefix,
      });

      /* as per https://www.gs1-128.info/sscc-18/ serial reference must be a total of 16 digits
       * Serial reference is GS1 + tagNumber in our system.
       */
      if (!tagNumber) {
        console.error('Missing tagNumber for SSCC-18 barcode generation');
        throw new Error('Tag number is required for SSCC-18 barcode');
      }

      const paddedGS1 = gs1CompanyPrefix.padStart(7, '0'); // gs1 can be 7-10 digits.
      const serialDigits = 16 - paddedGS1.length; // reference number can be 6-9 digits.
      const paddedTagNumber = tagNumber.padStart(serialDigits, '0');

      const result = `(00) 0 ${paddedGS1} ${paddedTagNumber}`;
      console.debug('SSCC-18 barcode generated', { paddedGS1, serialDigits, paddedTagNumber, result });

      return result;
    },
    props: { includecheck: true, includecheckintext: true, textxalign: 'center' },
  },
};

/** The organization fields a GS1 / SSCC-18 barcode reads; undefined until the settings read answers. */
export type BarcodeOrgInfo = { gs1CompanyPrefix?: number | null } | null | undefined;

/**
 * Generates barcode properties based on barcode type and content
 *
 * @param tagNumber - The tag number to encode in the barcode
 * @param options - Configuration options for the barcode
 * @returns Properties object for the bwip-js barcode renderer
 */
export function getBarcodeProps(
  tagNumber: string,
  options: {
    type?: 'upc' | 'gs1' | 'sscc18' | string;
    numberSystem?: string;
    textTemplate?: string;
    gtinCode?: string;
    packDate?: string;
    orgInfo?: BarcodeOrgInfo;
    lotNumber?: string;
    productCode?: string;
    productGS1?: string;
    customFields?: { [key: string]: string | number };
  }
) {
  const { type = 'code128', textTemplate } = options;
  const tagType = barcodeTypes[type];

  // Handle default or unknown barcode type
  if (!tagType) {
    const result = {
      text: tagNumber,
      includetext: !!textTemplate,
      alttext: textTemplate ? Mustache.render(textTemplate, { tagNumber }) : '',
      bcid: tagType?.bcid ?? type,
      textxalign: 'center',
    };
    return result;
  }

  // Validate GS1 company prefix if required
  const companyPrefix = options.productGS1 ?? options.orgInfo?.gs1CompanyPrefix;

  if (tagType.requiresGS1 && !companyPrefix) {
    console.error('Missing GS1 company prefix for barcode type that requires it', { type });
    throw new Error('SET GS1 COMPANY PREFIX IN SETTINGS');
  }

  try {
    // Generate barcode text using the appropriate generator
    const text = tagType.generate({
      tagNumber,
      gs1CompanyPrefix: companyPrefix?.toString(),
      numberSystem: options.numberSystem,
      gtinCode: options.gtinCode,
      packDate: options.packDate,
      lotNumber: options.lotNumber,
      productCode: options.productCode,
      customFields: options.customFields,
    });

    // Return properties for the barcode renderer
    const result = { text, includetext: !!text, bcid: tagType.bcid, ...tagType.props };
    return result;
  } catch (error) {
    console.error('Error generating barcode text', { error, type, tagNumber });
    throw error;
  }
}
